import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';
import { ADMIN_PERMISSIONS, TV_SCREEN_INFO, type Permission } from '@codepass/shared';
import { AppShell } from '@/components/AppShell';
import { useAuth } from '@/auth/AuthProvider';
import { HomeRedirect, ProtectedRoute, RoleGuard } from '@/auth/guards';
import { LoginPage } from '@/pages/Login';
import { AuthCallbackPage } from '@/pages/AuthCallback';
import { DashboardPage } from '@/pages/Dashboard';
import { TvDashboardPage } from '@/pages/TvDashboard';
import { TvInternalPage } from '@/pages/TvInternal';
import { TvProjectsPage } from '@/pages/TvProjects';
import { ContractsPage } from '@/pages/Contracts';
import { ContractDetailPage } from '@/pages/ContractDetail';
import { HoursPage } from '@/pages/Hours';
import { InvoicesPage, InvoiceHistoryPage } from '@/pages/Invoices';
import { InvoiceNewPage } from '@/pages/InvoiceNew';
import { InvoiceDetailPage } from '@/pages/InvoiceDetail';
import { PartnerCommissionPage } from '@/pages/PartnerCommission';
import { SubscriptionsPage } from '@/pages/Subscriptions';
import { AlertsPage, AlertArchivePage } from '@/pages/Alerts';
import { AlertDetailPage } from '@/pages/AlertDetail';
import { CustomersPage } from '@/pages/Customers';
import { CustomerDetailPage } from '@/pages/CustomerDetail';
import { ReportsPage } from '@/pages/Reports';
import { ReportPage } from '@/pages/ReportPage';
import { BudgetPage } from '@/pages/Budget';
import { ProfilePage } from '@/pages/Profile';
import { AdminHubPage } from '@/pages/admin/Hub';
import { AdminUsersPage } from '@/pages/admin/Users';
import { AdminRolesPage } from '@/pages/admin/Roles';
import { AdminIntegrationsPage } from '@/pages/admin/Integrations';
import { AdminRulesPage } from '@/pages/admin/Rules';
import { AdminAuditPage } from '@/pages/admin/Audit';
import { AdminTvDashboardPage } from '@/pages/admin/TvDashboardEditor';
import { ForbiddenPage, NotFoundPage } from '@/pages/Errors';

/**
 * Kortform: siden bag en rettighed - eller mindst én af flere. Hvilke der findes, staar i
 * PERMISSIONS i @codepass/shared; hvem der har dem, rettes paa /admin/roller.
 */
function guarded(permission: Permission | readonly Permission[], page: ReactNode) {
  return <RoleGuard permission={permission}>{page}</RoleGuard>;
}

/** Alle fire alarmrettigheder giver 'Se mine alarmer' med sig - se `requires`. */
const ALERTS: readonly Permission[] = ['alerts.view_own'];
const ALERT_ARCHIVE: readonly Permission[] = ['alerts.archive_own'];
const INVOICE_PAGE: readonly Permission[] = ['invoices.drafts.view', 'invoices.history.view'];
const CONTRACTS: readonly Permission[] = ['contracts.view', 'service_agreements.view'];

/** Rapporterne er ikke en rettighed, men en liste pr. rolle. */
function ReportsGuard({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return <RoleGuard allowed={(user?.reports.length ?? 0) > 0}>{children}</RoleGuard>;
}

/**
 * Ruterne er danske - de er brugervendte og bookmarkbare. API-stierne er engelske.
 * Bevidst brud paa ellers ensartet sprog, jf. PRD par. 3.2.
 */
export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Engelsk sti: den skal matche redirect-URL'en registreret i Azure og Supabase. */}
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      {/*
        /skift-adgangskode er fjernet. Login gaar gennem Microsoft, saa adgangskoden hoerer
        hjemme hos Microsoft - Codepass har ingen at skifte. Gamle bogmaerker sendes til
        profilen, hvor det staar forklaret.
      */}
      <Route path="/skift-adgangskode" element={<Navigate to="/profil" replace />} />

      {/*
        TV-skærmen. Uden for ProtectedRoute og AppShell: ingen session, ingen menu, ingen
        brugermenu - kun tallene. Adgangen er token og IP, som API'et afgør. Engelsk sti ligesom
        /dashboard, som den hører under.
      */}
      <Route path={TV_SCREEN_INFO.growth.path} element={<TvDashboardPage />} />
      <Route path={TV_SCREEN_INFO.projects.path} element={<TvProjectsPage />} />
      <Route path={TV_SCREEN_INFO.internal.path} element={<TvInternalPage />} />

      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<HomeRedirect />} />
        {/* Login og fejlsiderne sender hertil. Uden adgang er det brugerens egen startside. */}
        <Route
          path="/dashboard"
          element={
            <RoleGuard permission="dashboard.view" fallback="home">
              <DashboardPage />
            </RoleGuard>
          }
        />

        {/* Projektaftalerne og serviceaftalerne er hver sin fane - se ContractsPage. */}
        <Route path="/kontrakter" element={guarded(CONTRACTS, <ContractsPage />)} />
        <Route path="/kontrakter/:id" element={guarded(CONTRACTS, <ContractDetailPage />)} />

        {/* Timer har kun én side. Undersiderne - kladden, Afsluttede, Godkendte og Afviste - er
            faldet én ad gangen, og ingen af dem blev brugt: de var oversigten med hvert sit
            faste filter, og beslutningen bag Godkendt og Afvist blev truffet i OnePro og ikke
            her. Se hovedkommentaren i apps/api/src/routes/time-entries.ts. */}
        <Route path="/timer" element={guarded('hours.view', <HoursPage />)} />

        <Route path="/fakturering" element={guarded('invoices.drafts.view', <InvoicesPage />)} />
        <Route path="/fakturering/ny" element={guarded('invoices.create', <InvoiceNewPage />)} />
        <Route
          path="/fakturering/historik"
          element={guarded('invoices.history.view', <InvoiceHistoryPage />)}
        />
        <Route
          path="/fakturering/abonnementer"
          element={guarded('subscriptions.view', <SubscriptionsPage />)}
        />
        {/* Hvad man maa paa siden - godkende, mailopsaetning, Excel - er hver sin rettighed. */}
        <Route
          path="/fakturering/provision"
          element={guarded('partner_commission.view', <PartnerCommissionPage />)}
        />
        {/* En kladde eller en sendt faktura - API'et afgoer, hvilken brugeren maa se. */}
        <Route path="/fakturering/:id" element={guarded(INVOICE_PAGE, <InvoiceDetailPage />)} />

        {/* Hvis alarmer man ser - egne eller alles - afgoer API'et. Se `alarmAccess`. */}
        <Route path="/alarmer" element={guarded(ALERTS, <AlertsPage />)} />
        <Route path="/alarmer/arkiv" element={guarded(ALERT_ARCHIVE, <AlertArchivePage />)} />
        <Route path="/alarmer/:id" element={guarded(ALERTS, <AlertDetailPage />)} />

        <Route path="/kunder" element={guarded('customers.view', <CustomersPage />)} />
        <Route path="/kunder/:id" element={guarded('customers.view', <CustomerDetailPage />)} />

        <Route
          path="/rapporter"
          element={
            <ReportsGuard>
              <ReportsPage />
            </ReportsGuard>
          }
        />
        {/* Alle rapporter - maanedlig, likviditet, wip, tillaeg ... - se REPORTS i @codepass/shared.
            Den enkelte rapport tjekker ReportPage selv. */}
        <Route
          path="/rapporter/:path"
          element={
            <ReportsGuard>
              <ReportPage />
            </ReportsGuard>
          }
        />

        <Route path="/budget" element={guarded('budget.view', <BudgetPage />)} />

        <Route path="/profil" element={<ProfilePage />} />

        {/* Hvert omraade har sin 'Se'; siderne viser selv, om man ogsaa kan aendre. */}
        <Route path="/admin" element={guarded(ADMIN_PERMISSIONS, <AdminHubPage />)} />
        <Route path="/admin/brugere" element={guarded('users.view', <AdminUsersPage />)} />
        <Route path="/admin/roller" element={guarded('roles.view', <AdminRolesPage />)} />
        <Route
          path="/admin/integrationer"
          element={guarded('integrations.view', <AdminIntegrationsPage />)}
        />
        <Route path="/admin/regler" element={guarded('rules.view', <AdminRulesPage />)} />
        <Route
          path="/admin/tv-dashboard"
          element={guarded('tv_dashboard.view', <AdminTvDashboardPage />)}
        />
        <Route path="/admin/audit" element={guarded('audit.view', <AdminAuditPage />)} />

        <Route path="/forbudt" element={<ForbiddenPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
